import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import {
    BookOpen,
    CalendarDays,
    ChevronDown,
    ChevronRight,
    Lock,
    Pencil,
    Plus,
    RefreshCcw,
    Unlock
} from "lucide-react";
import { payrollFetchResourceRequest, payrollSaveResourceRequest } from "../../../redux/actions/payrollActions";
import "./PaieCyclePages.css";

const currentMonth = new Date().getMonth() + 1;
const currentYear = new Date().getFullYear();
const defaultExercice = {
    nom: `Exercice paie ${currentYear}`,
    dateDebut: `${currentYear}-01-01`,
    dateFin: `${currentYear}-12-31`,
    annee: currentYear,
};

const formatDate = (dateVal) => {
    if (!dateVal) return "-";
    try {
        if (Array.isArray(dateVal)) {
            const [y, m, day] = dateVal;
            return `${String(day).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
        }
        const d = new Date(dateVal);
        if (isNaN(d.getTime())) return String(dateVal);
        return d.toLocaleDateString("fr-FR");
    } catch (e) {
        return String(dateVal);
    }
};

const isEarlierPeriod = (candidate, period) =>
    candidate.annee < period.annee || (candidate.annee === period.annee && candidate.mois < period.mois);

const isLaterPeriod = (candidate, period) =>
    candidate.annee > period.annee || (candidate.annee === period.annee && candidate.mois > period.mois);

const formatPeriod = (period) => `${String(period.mois).padStart(2, "0")}/${period.annee}`;

const PeriodesPaiePage = () => {
    const dispatch = useDispatch();
    const { exercicesPaie = [], periodes = [], loading, saving } = useSelector((state) => state.payroll || {});

    // Form visibility states
    const [showExerciceForm, setShowExerciceForm] = useState(false);
    const [showPeriodeForm, setShowPeriodeForm] = useState(false);

    const [periodeForm, setPeriodeForm] = useState({
        mois: currentMonth,
        annee: currentYear,
        statut: "OUVERTE",
        exerciceId: "",
        motifOuverture: ""
    });
    const [exerciceForm, setExerciceForm] = useState(defaultExercice);
    const [editingPeriode, setEditingPeriode] = useState(null);
    const [editingExercice, setEditingExercice] = useState(null);

    // Expanded state for exercise accordions
    const [expandedExercices, setExpandedExercices] = useState({});

    useEffect(() => {
        dispatch(payrollFetchResourceRequest("exercicesPaie"));
        dispatch(payrollFetchResourceRequest("periodes"));
    }, [dispatch]);

    // Automatically expand the first exercise or when exercises load
    useEffect(() => {
        if (exercicesPaie.length > 0 && Object.keys(expandedExercices).length === 0) {
            setExpandedExercices({ [exercicesPaie[0].id]: true });
        }
    }, [exercicesPaie]);

    const toggleExpand = (id) => {
        setExpandedExercices((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const savePeriode = () => {
        if (!periodeForm.mois || !periodeForm.annee || !periodeForm.statut) {
            Swal.fire({ icon: "warning", text: "Mois, annee et statut sont obligatoires." });
            return;
        }
        dispatch(payrollSaveResourceRequest("periodes", {
            mois: Number(periodeForm.mois),
            annee: Number(periodeForm.annee),
            statut: periodeForm.statut,
            dateCloture: periodeForm.statut === "CLOTUREE" ? new Date().toISOString() : null,
            exerciceId: periodeForm.exerciceId ? Number(periodeForm.exerciceId) : null,
            motifOuverture: periodeForm.motifOuverture || null
        }, editingPeriode?.id || null));

        setEditingPeriode(null);
        setShowPeriodeForm(false);
        setPeriodeForm({
            mois: currentMonth,
            annee: currentYear,
            statut: "OUVERTE",
            exerciceId: exercicesPaie[0]?.id ? String(exercicesPaie[0].id) : "",
            motifOuverture: ""
        });
    };

    const editPeriode = (periode) => {
        setEditingPeriode(periode);
        setPeriodeForm({
            mois: periode.mois,
            annee: periode.annee,
            statut: periode.statut,
            exerciceId: periode.exerciceId ? String(periode.exerciceId) : "",
            motifOuverture: periode.motifOuverture || ""
        });
        setShowPeriodeForm(true);
        setShowExerciceForm(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const saveExercice = () => {
        if (!exerciceForm.nom || !exerciceForm.dateDebut || !exerciceForm.dateFin || !exerciceForm.annee) {
            Swal.fire({ icon: "warning", text: "Nom, dates et annee sont obligatoires." });
            return;
        }
        dispatch(payrollSaveResourceRequest("exercicesPaie", {
            nom: exerciceForm.nom,
            dateDebut: exerciceForm.dateDebut,
            dateFin: exerciceForm.dateFin,
            annee: Number(exerciceForm.annee),
        }, editingExercice?.id || null));

        setEditingExercice(null);
        setShowExerciceForm(false);
        setExerciceForm(defaultExercice);
    };

    const handleEditExercice = (exercice, e) => {
        e.stopPropagation(); // Avoid toggling accordion
        setEditingExercice(exercice);
        setExerciceForm({
            nom: exercice.nom || "",
            dateDebut: exercice.dateDebut || "",
            dateFin: exercice.dateFin || "",
            annee: exercice.annee || currentYear,
        });
        setShowExerciceForm(true);
        setShowPeriodeForm(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const closePeriod = async (periode, e) => {
        e.stopPropagation();
        const blockingPeriod = periodes
            .filter((candidate) => isEarlierPeriod(candidate, periode) && candidate.statut !== "CLOTUREE")
            .sort((a, b) => b.annee - a.annee || b.mois - a.mois)[0];
        if (blockingPeriod) {
            Swal.fire({
                icon: "warning",
                title: "Clôture impossible",
                text: `Clôturez d'abord la période ${formatPeriod(blockingPeriod)} avant de clôturer ${formatPeriod(periode)}.`
            });
            return;
        }
        const confirm = await Swal.fire({
            icon: "warning",
            title: "Clôturer la période ?",
            text: `La période ${String(periode.mois).padStart(2, "0")}/${periode.annee} passera au statut CLOTUREE.`,
            showCancelButton: true,
            confirmButtonColor: "#334785",
            cancelButtonText: "Annuler",
            confirmButtonText: "Clôturer",
        });
        if (!confirm.isConfirmed) return;
        dispatch(payrollSaveResourceRequest("periodes", {
            mois: periode.mois,
            annee: periode.annee,
            statut: "CLOTUREE",
            dateCloture: new Date().toISOString(),
            exerciceId: periode.exerciceId || null,
            motifOuverture: periode.motifOuverture || null
        }, periode.id));
    };

    const reopenPeriod = async (periode, e) => {
        e.stopPropagation();
        const laterProcessedPeriod = periodes
            .filter((candidate) => isLaterPeriod(candidate, periode) && ["EN_COURS_CALCUL", "CALCULEE", "CLOTUREE"].includes(candidate.statut))
            .sort((a, b) => a.annee - b.annee || a.mois - b.mois)[0];
        if (laterProcessedPeriod) {
            Swal.fire({
                icon: "warning",
                title: "Réouverture impossible",
                text: `La période ${formatPeriod(laterProcessedPeriod)} est déjà traitée. Réouvrez-la d'abord avant de réouvrir ${formatPeriod(periode)}.`
            });
            return;
        }
        const { value: motif } = await Swal.fire({
            icon: "question",
            title: "Réouvrir la période ?",
            text: `Saisissez le motif de réouverture pour la période ${String(periode.mois).padStart(2, "0")}/${periode.annee} :`,
            input: "text",
            inputPlaceholder: "Motif de réouverture obligatoire...",
            showCancelButton: true,
            confirmButtonColor: "#204385",
            cancelButtonText: "Annuler",
            confirmButtonText: "Réouvrir",
            inputValidator: (value) => {
                if (!value) {
                    return "Vous devez saisir un motif pour réouvrir la période !";
                }
            }
        });
        if (!motif) return;

        dispatch(payrollSaveResourceRequest("periodes", {
            mois: periode.mois,
            annee: periode.annee,
            statut: "OUVERTE",
            dateCloture: null,
            exerciceId: periode.exerciceId || null,
            motifOuverture: motif
        }, periode.id));
    };

    // Group periods by exercise ID
    const periodsByExercice = useMemo(() => {
        const groups = {};
        periodes.forEach((p) => {
            const key = p.exerciceId ? String(p.exerciceId) : "sans-exercice";
            if (!groups[key]) groups[key] = [];
            groups[key].push(p);
        });
        return groups;
    }, [periodes]);

    return (
        <div className="pcyc-page">
            <div className="pcyc-header">
                <div>
                    <h2><CalendarDays size={18} />Exercices et périodes de paie</h2>
                    <p>Gestion unifiée des exercices de paie et de leurs périodes mensuelles associées.</p>
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                    <button
                        className="pcyc-btn pcyc-btn-light"
                        type="button"
                        onClick={() => {
                            setShowExerciceForm(!showExerciceForm);
                            setShowPeriodeForm(false);
                            setEditingExercice(null);
                            setExerciceForm(defaultExercice);
                        }}
                    >
                        <Plus size={15} /> Exercice
                    </button>
                    <button
                        className="pcyc-btn pcyc-btn-light"
                        type="button"
                        onClick={() => {
                            setShowPeriodeForm(!showPeriodeForm);
                            setShowExerciceForm(false);
                            setEditingPeriode(null);
                            setPeriodeForm({
                                mois: currentMonth,
                                annee: currentYear,
                                statut: "OUVERTE",
                                exerciceId: exercicesPaie[0]?.id ? String(exercicesPaie[0].id) : "",
                                motifOuverture: ""
                            });
                        }}
                    >
                        <Plus size={15} /> Période
                    </button>
                    <button className="pcyc-btn pcyc-btn-blue" type="button" onClick={() => {
                        dispatch(payrollFetchResourceRequest("exercicesPaie"));
                        dispatch(payrollFetchResourceRequest("periodes"));
                    }}>
                        <RefreshCcw size={15} /> Actualiser
                    </button>
                </div>
            </div>

            {/* ───────────── FORMULAIRE EXERCICE ───────────── */}
            {showExerciceForm && (
                <div className="pcyc-card pcyc-card-margin">
                    <div className="pcyc-card-title">
                        <Plus size={16} />
                        {editingExercice ? "Modifier l'exercice" : "Nouvel exercice"}
                    </div>
                    <div className="pcyc-form-row pcyc-form-row-exercice">
                        <label>
                            <span>Nom</span>
                            <input
                                value={exerciceForm.nom}
                                onChange={(e) => setExerciceForm({ ...exerciceForm, nom: e.target.value })}
                            />
                        </label>
                        <label>
                            <span>Année</span>
                            <input
                                type="number"
                                value={exerciceForm.annee}
                                onChange={(e) => setExerciceForm({ ...exerciceForm, annee: e.target.value })}
                            />
                        </label>
                        <label>
                            <span>Date début</span>
                            <input
                                type="date"
                                value={exerciceForm.dateDebut}
                                onChange={(e) => setExerciceForm({ ...exerciceForm, dateDebut: e.target.value })}
                            />
                        </label>
                        <label>
                            <span>Date fin</span>
                            <input
                                type="date"
                                value={exerciceForm.dateFin}
                                onChange={(e) => setExerciceForm({ ...exerciceForm, dateFin: e.target.value })}
                            />
                        </label>
                        <div className="pcyc-form-row-actions">
                            <button
                                className="pcyc-btn pcyc-btn-light"
                                type="button"
                                onClick={() => {
                                    setShowExerciceForm(false);
                                    setEditingExercice(null);
                                    setExerciceForm(defaultExercice);
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                className="pcyc-btn pcyc-btn-primary"
                                type="button"
                                disabled={saving}
                                onClick={saveExercice}
                            >
                                {editingExercice ? "Enregistrer" : "Créer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ───────────── FORMULAIRE PERIODE ───────────── */}
            {showPeriodeForm && (
                <div className="pcyc-card pcyc-card-margin">
                    <div className="pcyc-card-title">
                        <Plus size={16} />
                        {editingPeriode ? "Modifier la période" : "Nouvelle période"}
                    </div>
                    <div className="pcyc-form-row pcyc-form-row-periode">
                        <label>
                            <span>Exercice</span>
                            <select
                                value={periodeForm.exerciceId}
                                onChange={(e) => setPeriodeForm({ ...periodeForm, exerciceId: e.target.value })}
                            >
                                <option value="">Sans exercice</option>
                                {exercicesPaie.map((exercice) => (
                                    <option key={exercice.id} value={exercice.id}>{exercice.nom}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span>Mois</span>
                            <input
                                type="number"
                                min="1"
                                max="12"
                                value={periodeForm.mois}
                                onChange={(e) => setPeriodeForm({ ...periodeForm, mois: e.target.value })}
                            />
                        </label>
                        <label>
                            <span>Année</span>
                            <input
                                type="number"
                                value={periodeForm.annee}
                                onChange={(e) => setPeriodeForm({ ...periodeForm, annee: e.target.value })}
                            />
                        </label>
                        <label>
                            <span>Statut</span>
                            <select
                                value={periodeForm.statut}
                                onChange={(e) => setPeriodeForm({ ...periodeForm, statut: e.target.value })}
                            >
                                <option value="OUVERTE">OUVERTE</option>
                                <option value="EN_COURS_CALCUL">EN_COURS_CALCUL</option>
                                <option value="CALCULEE">CALCULEE</option>
                                <option value="CLOTUREE">CLOTUREE</option>
                            </select>
                        </label>
                        <div className="pcyc-form-row-actions">
                            <button
                                className="pcyc-btn pcyc-btn-light"
                                type="button"
                                onClick={() => {
                                    setShowPeriodeForm(false);
                                    setEditingPeriode(null);
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                className="pcyc-btn pcyc-btn-primary"
                                type="button"
                                disabled={saving}
                                onClick={savePeriode}
                            >
                                {editingPeriode ? "Enregistrer" : "Créer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ───────────── LISTE DES EXERCICES ACCORDEONS ───────────── */}
            <div className="pcyc-exercices-list">
                {exercicesPaie.length === 0 ? (
                    <div className="pcyc-card pcyc-empty" style={{ textAlign: "center", padding: "30px" }}>
                        Aucun exercice enregistré. Commencez par en ajouter un.
                    </div>
                ) : (
                    exercicesPaie.map((exercice) => {
                        const isExpanded = !!expandedExercices[exercice.id];
                        const key = String(exercice.id);
                        const exercicePeriods = periodsByExercice[key] || [];

                        return (
                            <div className="pcyc-exercice-item" key={exercice.id}>
                                <div className="pcyc-exercice-header" onClick={() => toggleExpand(exercice.id)}>
                                    <div className="pcyc-exercice-info">
                                        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                        <div className="pcyc-exercice-title-group">
                                            <h3>{exercice.nom}</h3>
                                            <span className="pcyc-exercice-meta">Année : {exercice.annee}</span>
                                        </div>
                                    </div>
                                    <div className="pcyc-exercice-details">
                                        <div className="pcyc-exercice-detail-item">
                                            Début : <strong>{formatDate(exercice.dateDebut)}</strong>
                                        </div>
                                        <div className="pcyc-exercice-detail-item">
                                            Fin : <strong>{formatDate(exercice.dateFin)}</strong>
                                        </div>
                                        <div className="pcyc-exercice-detail-item">
                                            Périodes : <strong>{exercicePeriods.length}</strong>
                                        </div>
                                    </div>
                                    <div className="pcyc-exercice-actions">
                                        <button
                                            type="button"
                                            onClick={(e) => handleEditExercice(exercice, e)}
                                            title="Modifier l'exercice"
                                        >
                                            <Pencil size={14} />
                                        </button>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="pcyc-exercice-body">
                                        <div className="pcyc-sub-table-title">
                                            <BookOpen size={14} /> Périodes rattachées à cet exercice
                                        </div>
                                        {exercicePeriods.length === 0 ? (
                                            <p className="pcyc-empty" style={{ margin: "10px 0 0 0" }}>
                                                Aucune période pour cet exercice.
                                            </p>
                                        ) : (
                                            <table className="pcyc-sub-table">
                                                <thead>
                                                    <tr>
                                                        <th>Période</th>
                                                        <th>Statut</th>
                                                        <th>Date clôture</th>
                                                        <th>Motif de réouverture</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {exercicePeriods.map((periode) => (
                                                        <tr key={periode.id}>
                                                            <td>
                                                                <span className="pcyc-code">
                                                                    {String(periode.mois).padStart(2, "0")}/{periode.annee}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <span className={`pcyc-status ${periode.statut}`}>
                                                                    {periode.statut}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                {periode.dateCloture
                                                                    ? new Date(periode.dateCloture).toLocaleString("fr-FR")
                                                                    : "-"}
                                                            </td>
                                                            <td>
                                                                {periode.motifOuverture ? (
                                                                    <span className="pcyc-motif-text">
                                                                        {periode.motifOuverture}
                                                                    </span>
                                                                ) : (
                                                                    "-"
                                                                )}
                                                            </td>
                                                            <td className="pcyc-row-actions">
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => editPeriode(periode, e)}
                                                                    title="Modifier la période"
                                                                >
                                                                    <Pencil size={14} />
                                                                </button>
                                                                {periode.statut === "CLOTUREE" ? (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => reopenPeriod(periode, e)}
                                                                        title="Réouvrir la période"
                                                                        style={{ color: "#204385" }}
                                                                    >
                                                                        <Unlock size={14} />
                                                                    </button>
                                                                ) : (
                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => closePeriod(periode, e)}
                                                                        title="Clôturer la période"
                                                                        style={{ color: "#334785" }}
                                                                    >
                                                                        <Lock size={14} />
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}

                {/* ───────────── SANS EXERCICE (OPTIONNEL) ───────────── */}
                {periodsByExercice["sans-exercice"] && periodsByExercice["sans-exercice"].length > 0 && (
                    <div className="pcyc-exercice-item">
                        <div
                            className="pcyc-exercice-header"
                            onClick={() => toggleExpand("sans-exercice")}
                            style={{ borderLeft: "4px solid #334785" }}
                        >
                            <div className="pcyc-exercice-info">
                                {expandedExercices["sans-exercice"] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                <div className="pcyc-exercice-title-group">
                                    <h3>Périodes hors exercice</h3>
                                    <span className="pcyc-exercice-meta">Périodes non associées à un exercice</span>
                                </div>
                            </div>
                            <div className="pcyc-exercice-details">
                                <div className="pcyc-exercice-detail-item">
                                    Périodes : <strong>{periodsByExercice["sans-exercice"].length}</strong>
                                </div>
                            </div>
                            <div className="pcyc-exercice-actions" />
                        </div>

                        {expandedExercices["sans-exercice"] && (
                            <div className="pcyc-exercice-body">
                                <table className="pcyc-sub-table">
                                    <thead>
                                        <tr>
                                            <th>Période</th>
                                            <th>Statut</th>
                                            <th>Date clôture</th>
                                            <th>Motif de réouverture</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {periodsByExercice["sans-exercice"].map((periode) => (
                                            <tr key={periode.id}>
                                                <td>
                                                    <span className="pcyc-code">
                                                        {String(periode.mois).padStart(2, "0")}/{periode.annee}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`pcyc-status ${periode.statut}`}>
                                                        {periode.statut}
                                                    </span>
                                                </td>
                                                <td>
                                                    {periode.dateCloture
                                                        ? new Date(periode.dateCloture).toLocaleString("fr-FR")
                                                        : "-"}
                                                </td>
                                                <td>
                                                    {periode.motifOuverture ? (
                                                        <span className="pcyc-motif-text">
                                                            {periode.motifOuverture}
                                                        </span>
                                                    ) : (
                                                        "-"
                                                    )}
                                                </td>
                                                <td className="pcyc-row-actions">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => editPeriode(periode, e)}
                                                        title="Modifier la période"
                                                    >
                                                        <Pencil size={14} />
                                                    </button>
                                                    {periode.statut === "CLOTUREE" ? (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => reopenPeriod(periode, e)}
                                                            title="Réouvrir la période"
                                                            style={{ color: "#204385" }}
                                                        >
                                                            <Unlock size={14} />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => closePeriod(periode, e)}
                                                            title="Clôturer la période"
                                                            style={{ color: "#334785" }}
                                                        >
                                                            <Lock size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PeriodesPaiePage;
